import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { kr, dato } from '../lib/format';
import { PageHeader, Card, Modal, Tabs } from '../components/ui';
import {
  Plus, Pencil, Trash2, Users, Calendar, FileText,
  Send, Download, CheckCircle2, RefreshCw, Wallet, AlertTriangle,
  X, User as UserIcon, Briefcase, Receipt, ChevronRight, Copy,
} from 'lucide-react';

const MND_NAVN = ['', 'Januar', 'Februar', 'Mars', 'April', 'Mai', 'Juni',
                  'Juli', 'August', 'September', 'Oktober', 'November', 'Desember'];

const STATUS_BADGE = {
  utkast: 'badge-warning', utbetalt: 'badge-success',
  sendt: 'badge-info', godkjent: 'badge-success', annullert: 'badge-neutral',
};

const Lonn = () => {
  const [tab, setTab] = useState('ansatte');
  return (
    <>
      <PageHeader title="Lønn" subtitle="Ansatte, lønnsslipper og a-melding til Skatteetaten" />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: 'ansatte',      label: 'Ansatte' },
          { key: 'lonnsslipper', label: 'Lønnsslipper' },
          { key: 'amelding',     label: 'A-melding' },
          { key: 'otp',          label: 'OTP / pensjon' },
        ]}
      />
      {tab === 'ansatte'      && <Ansatte />}
      {tab === 'lonnsslipper' && <Lonnsslipper />}
      {tab === 'amelding'     && <Ameldinger />}
      {tab === 'otp'          && <OtpRapport />}
    </>
  );
};

// ── Ansatte ─────────────────────────────────────────────────────────────────
const tomA = () => ({
  fornavn: '', etternavn: '', fnr: '', epost: '', telefon: '', adresse: '',
  kontonummer: '', stilling: '', yrkeskode: '', ansatt_dato: '',
  arbeidsforhold_type: 'ordinaertArbeidsforhold',
  stillingsprosent: 100, timer_pr_uke: 37.5,
  manedslonn: 0, timelonn: 0,
  skattekort_prosent: 0, skattekort_tabell: '',
  aga_sone: '1', aga_sats: 14.1, feriepenge_sats: 12.0,
  otp_aktiv: false, otp_sats: 2.0,
  konto_lonn: '5000', notater: '',
});

const Ansatte = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tomA());
  const [detalj, setDetalj] = useState(null);

  const last = () => api.get('/ansatte', { params: { inkl_inaktiv: 1 } }).then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);

  const aapneNy = () => { setRedigerer(null); setSkjema(tomA()); setOpen(true); };
  const aapneRed = (a) => { setRedigerer(a); setSkjema({ ...a }); setOpen(true); };

  const lagre = async () => {
    if (!skjema.fornavn || !skjema.etternavn) {
      alert('Fornavn og etternavn må fylles inn.'); return;
    }
    try {
      if (redigerer) await api.put(`/ansatte/${redigerer.id}`, skjema);
      else await api.post('/ansatte', skjema);
      setOpen(false); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slett = async (a) => {
    if (!window.confirm(`Slette ${a.navn}?`)) return;
    try { await api.delete(`/ansatte/${a.id}`); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex justify-between items-center">
          <span className="fm-text-muted" style={{ fontSize: 12 }}>{liste.length} ansatte</span>
          <button className="btn-primary" onClick={aapneNy}><Plus size={14} /> Ny ansatt</button>
        </div>
      </Card>

      <Card style={{ padding: 0 }}>
        {liste.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Users size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
            <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>Ingen ansatte registrert.</p>
          </div>
        ) : (
          <table>
            <thead><tr>
              <th>Navn</th><th>Stilling</th><th>F.nr</th><th>Kontonummer</th>
              <th style={{ textAlign: 'right' }}>Mnd.lønn</th>
              <th>AGA-sone</th><th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {liste.map((a) => (
                <tr key={a.id} style={{ opacity: a.aktiv ? 1 : 0.5, cursor: 'pointer' }}
                    onClick={() => setDetalj(a)}>
                  <td><b style={{ color: 'var(--theme-accent)' }}>{a.navn}</b></td>
                  <td>{a.stilling || '—'}</td>
                  <td className="fm-text-muted" style={{ fontSize: 11 }}>
                    {a.fnr ? `${a.fnr.slice(0,6)}*****` : '—'}
                  </td>
                  <td>{a.kontonummer || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{kr(a.manedslonn)}</td>
                  <td>{a.aga_sone} ({a.aga_sats}%)</td>
                  <td>{a.aktiv ? <span className="badge badge-success">aktiv</span>
                               : <span className="badge badge-neutral">inaktiv</span>}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <button className="btn-ghost" onClick={() => aapneRed(a)}><Pencil size={14} /></button>
                    <button className="btn-ghost" onClick={() => slett(a)}>
                      <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)}
             title={redigerer ? 'Rediger ansatt' : 'Ny ansatt'}
             width={720}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label>Fornavn *</label>
            <input value={skjema.fornavn} onChange={(e) => setSkjema({ ...skjema, fornavn: e.target.value })} /></div>
          <div><label>Etternavn *</label>
            <input value={skjema.etternavn} onChange={(e) => setSkjema({ ...skjema, etternavn: e.target.value })} /></div>
          <div><label>Fødselsnummer (11 siffer)</label>
            <input value={skjema.fnr} onChange={(e) => setSkjema({ ...skjema, fnr: e.target.value })} /></div>
          <div><label>Kontonummer</label>
            <input value={skjema.kontonummer} onChange={(e) => setSkjema({ ...skjema, kontonummer: e.target.value })} /></div>
          <div><label>E-post</label>
            <input type="email" value={skjema.epost} onChange={(e) => setSkjema({ ...skjema, epost: e.target.value })} /></div>
          <div><label>Telefon</label>
            <input value={skjema.telefon} onChange={(e) => setSkjema({ ...skjema, telefon: e.target.value })} /></div>
          <div style={{ gridColumn: '1 / -1' }}><label>Adresse</label>
            <input value={skjema.adresse} onChange={(e) => setSkjema({ ...skjema, adresse: e.target.value })} /></div>
          <div><label>Stilling</label>
            <input value={skjema.stilling} onChange={(e) => setSkjema({ ...skjema, stilling: e.target.value })} /></div>
          <div><label>Yrkeskode (STYRK-08)</label>
            <input value={skjema.yrkeskode} onChange={(e) => setSkjema({ ...skjema, yrkeskode: e.target.value })}
                   placeholder="f.eks. 2512116" /></div>
          <div><label>Ansatt fra</label>
            <input type="date" value={skjema.ansatt_dato} onChange={(e) => setSkjema({ ...skjema, ansatt_dato: e.target.value })} /></div>
          <div><label>Stillingsprosent</label>
            <input type="number" value={skjema.stillingsprosent}
                   onChange={(e) => setSkjema({ ...skjema, stillingsprosent: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Månedslønn (brutto)</label>
            <input type="number" step="0.01" value={skjema.manedslonn}
                   onChange={(e) => setSkjema({ ...skjema, manedslonn: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Timelønn</label>
            <input type="number" step="0.01" value={skjema.timelonn}
                   onChange={(e) => setSkjema({ ...skjema, timelonn: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Skattekort prosent (%)</label>
            <input type="number" step="0.1" value={skjema.skattekort_prosent}
                   onChange={(e) => setSkjema({ ...skjema, skattekort_prosent: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Skattekort tabell</label>
            <input value={skjema.skattekort_tabell}
                   onChange={(e) => setSkjema({ ...skjema, skattekort_tabell: e.target.value })}
                   placeholder="f.eks. 7100" /></div>
          <div><label>AGA-sone</label>
            <select value={skjema.aga_sone} onChange={(e) => {
              const s = e.target.value;
              const standard = { '1': 14.1, '1a': 14.1, '2': 10.6, '3': 6.4, '4': 5.1, '4a': 7.9, '5': 0 };
              setSkjema({ ...skjema, aga_sone: s, aga_sats: standard[s] ?? skjema.aga_sats });
            }}>
              <option value="1">Sone 1 (14.1%)</option>
              <option value="1a">Sone 1a (14.1%)</option>
              <option value="2">Sone 2 (10.6%)</option>
              <option value="3">Sone 3 (6.4%)</option>
              <option value="4">Sone 4 (5.1%)</option>
              <option value="4a">Sone 4a (7.9%)</option>
              <option value="5">Sone 5 (0%)</option>
            </select></div>
          <div><label>AGA-sats (%)</label>
            <input type="number" step="0.1" value={skjema.aga_sats}
                   onChange={(e) => setSkjema({ ...skjema, aga_sats: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Feriepenge-sats (%)</label>
            <input type="number" step="0.1" value={skjema.feriepenge_sats}
                   onChange={(e) => setSkjema({ ...skjema, feriepenge_sats: parseFloat(e.target.value || 0) })} /></div>
          <div><label>OTP / pensjon</label>
            <select value={skjema.otp_aktiv ? '1' : '0'}
                    onChange={(e) => setSkjema({ ...skjema, otp_aktiv: e.target.value === '1' })}>
              <option value="0">Ikke innmeldt</option>
              <option value="1">Innmeldt (OTP aktiv)</option>
            </select></div>
          <div><label>OTP-sats (%, min 2)</label>
            <input type="number" step="0.1" value={skjema.otp_sats}
                   onChange={(e) => setSkjema({ ...skjema, otp_sats: parseFloat(e.target.value || 0) })} /></div>
          <div><label>Lønnskonto</label>
            <input value={skjema.konto_lonn} onChange={(e) => setSkjema({ ...skjema, konto_lonn: e.target.value })} /></div>
          {redigerer && (
            <div><label>Aktiv</label>
              <select value={skjema.aktiv ? '1' : '0'}
                      onChange={(e) => setSkjema({ ...skjema, aktiv: e.target.value === '1' })}>
                <option value="1">Aktiv</option>
                <option value="0">Inaktiv (sluttet)</option>
              </select></div>
          )}
          <div style={{ gridColumn: '1 / -1' }}><label>Notater</label>
            <textarea rows={2} value={skjema.notater} onChange={(e) => setSkjema({ ...skjema, notater: e.target.value })} /></div>
        </div>
      </Modal>

      {detalj && (
        <AnsattDetalj ansattId={detalj.id}
                      onClose={() => setDetalj(null)}
                      onRediger={(a) => { setDetalj(null); aapneRed(a); }}
                      onSlett={async (a) => { await slett(a); setDetalj(null); }} />
      )}
    </>
  );
};

// ── Ansatt-detalj (Personalia · Arbeidsforhold · Skattekort · Lønnsslipper) ─
const Rad = ({ label, value }) => (
  <div style={{ display: 'flex', padding: '8px 0', borderBottom: '1px solid var(--theme-border-light, rgba(255,255,255,.06))' }}>
    <div className="fm-text-muted" style={{ flex: '0 0 42%', fontSize: 12 }}>{label}</div>
    <div style={{ flex: 1, fontSize: 13 }}>{value || <span className="fm-text-muted">—</span>}</div>
  </div>
);

const ARBFORHOLD_TEKST = {
  ordinaertArbeidsforhold: 'Vanlig',
  maritimtArbeidsforhold: 'Maritimt',
  frilanserOppdragstakerHonorarPersonerMm: 'Frilanser / oppdragstaker',
  pensjonEllerAndreYtelserUtenAnsettelsesforhold: 'Pensjon / ytelser u/ansettelse',
};

const AnsattDetalj = ({ ansattId, onClose, onRediger, onSlett }) => {
  const [a, setA] = useState(null);
  const [slipper, setSlipper] = useState([]);

  useEffect(() => {
    api.get(`/ansatte/${ansattId}`).then((r) => setA(r.data));
    api.get('/lonnsslipp', { params: { ansatt_id: ansattId } })
       .then((r) => setSlipper(r.data || []));
  }, [ansattId]);

  if (!a) return (
    <Modal open onClose={onClose} title="Laster…" width={960}><span className="fm-text-muted">Laster ansatt…</span></Modal>
  );

  const adresse = a.adresse || '';
  const skattekortGyldig = a.skattekort_tabell || a.skattekort_prosent
    ? `Tabell ${a.skattekort_tabell || '—'} · ${a.skattekort_prosent || 0}%`
    : 'Ikke registrert';

  const kopier = (verdi) => { if (verdi) navigator.clipboard?.writeText(verdi); };

  return (
    <Modal open onClose={onClose} width={960}
           title={
             <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
               <span>{a.navn}</span>
               <button className="btn-ghost" title="Kopier navn"
                       onClick={() => kopier(a.navn)}><Copy size={13} /></button>
               {!a.aktiv && <span className="badge badge-neutral">Sluttet</span>}
             </span>
           }
           footer={<>
             {a.aktiv && <button className="btn-ghost" onClick={() => onSlett(a)}
                                  style={{ color: 'var(--theme-danger)' }}>
                          <Trash2 size={14} /> Avslutt ansattforhold
                        </button>}
             <div style={{ flex: 1 }} />
             <button className="btn-ghost" onClick={onClose}>Lukk</button>
             <button className="btn-primary" onClick={() => onRediger(a)}>
               <Pencil size={14} /> Rediger
             </button>
           </>}>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 16 }}>

          {/* PERSONALIA */}
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600,
                         display: 'flex', alignItems: 'center', gap: 6 }}>
              <UserIcon size={14} /> Personalia
            </h3>
            <Rad label="Adresse" value={adresse.split('\n').map((l, i) => <div key={i}>{l}</div>)} />
            <Rad label="Fødselsnummer" value={a.fnr} />
            <Rad label="E-post" value={a.epost && <a href={`mailto:${a.epost}`}>{a.epost}</a>} />
            <Rad label="Telefon" value={a.telefon && <a href={`tel:${a.telefon}`}>{a.telefon}</a>} />
            <Rad label="Bankkonto" value={a.kontonummer} />
            <Rad label="Feriepenger" value={`${a.feriepenge_sats} %`} />
            <Rad label="Lønnskonto (bokf.)" value={a.konto_lonn} />
            <Rad label="Notat" value={a.notater} />
          </div>

          {/* ARBEIDSFORHOLD */}
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600,
                         display: 'flex', alignItems: 'center', gap: 6 }}>
              <Briefcase size={14} /> Arbeidsforhold: {a.stilling || '—'}
            </h3>
            <Rad label="Periode" value={
              a.ansatt_dato
                ? `${dato(a.ansatt_dato)}${a.sluttet_dato ? ' – ' + dato(a.sluttet_dato) : ''}`
                : ''} />
            <Rad label="Arbeidsforholdstype"
                 value={ARBFORHOLD_TEKST[a.arbeidsforhold_type] || a.arbeidsforhold_type} />
            <Rad label="Yrkeskode (STYRK-08)" value={a.yrkeskode} />
            <Rad label="Timer i en full uke" value={a.timer_pr_uke} />
            <Rad label="Stillingsprosent" value={`${a.stillingsprosent} %`} />
            <Rad label="Månedslønn" value={kr(a.manedslonn)} />
            <Rad label="Timelønn" value={a.timelonn ? kr(a.timelonn) : ''} />
            <Rad label="AGA-sone" value={`${a.aga_sone} (${a.aga_sats} %)`} />
            <Rad label="OTP / pensjon"
                 value={a.otp_aktiv ? `Aktiv (${a.otp_sats} %)` : 'Ikke innmeldt'} />
          </div>

          {/* SKATTEKORT */}
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600,
                         display: 'flex', alignItems: 'center', gap: 6 }}>
              <Receipt size={14} /> Skattekort
            </h3>
            <Rad label="Status" value={skattekortGyldig} />
            <Rad label="Tabell" value={a.skattekort_tabell} />
            <Rad label="Trekkprosent" value={a.skattekort_prosent ? `${a.skattekort_prosent} %` : ''} />
            <p className="fm-text-muted" style={{ fontSize: 11, marginTop: 8 }}>
              Hent oppdatert skattekort via Altinn på Admin-siden.
            </p>
          </div>

          {/* LØNNSSLIPPER */}
          <div>
            <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600,
                         display: 'flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} /> Lønnsslipper
              <span className="fm-text-muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
                {slipper.length} totalt
              </span>
            </h3>
            {slipper.length === 0 ? (
              <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>
                Ingen lønnsslipper registrert for denne ansatte.
              </p>
            ) : (
              <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                {slipper.map((s) => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 0',
                    borderBottom: '1px solid var(--theme-border-light, rgba(255,255,255,.06))',
                  }}>
                    <div style={{ flex: '0 0 90px', fontSize: 12 }}>
                      {MND_NAVN[s.maaned]} {s.aar}
                    </div>
                    <div style={{ flex: 1, fontSize: 12 }}>
                      <span className={`badge ${STATUS_BADGE[s.status] || 'badge-neutral'}`}>
                        {s.status || 'utkast'}
                      </span>
                      <span className="fm-text-muted" style={{ marginLeft: 8 }}>
                        {s.utbetalingsdato ? dato(s.utbetalingsdato) : 'Ikke utbetalt'}
                      </span>
                    </div>
                    <div style={{ flex: '0 0 110px', textAlign: 'right', fontSize: 12 }}>
                      <div>{kr(s.netto)}</div>
                      <div className="fm-text-muted" style={{ fontSize: 10 }}>netto</div>
                    </div>
                    <ChevronRight size={14} className="fm-text-muted" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
    </Modal>
  );
};

// ── Lønnsslipper ────────────────────────────────────────────────────────────
const Lonnsslipper = () => {
  const [aar, setAar] = useState(new Date().getFullYear());
  const [mnd, setMnd] = useState(new Date().getMonth() + 1);
  const [slipper, setSlipper] = useState(null);
  const [ansatte, setAnsatte] = useState([]);
  const [open, setOpen] = useState(false);
  const [valgtSlipp, setValgtSlipp] = useState(null);
  const [skjema, setSkjema] = useState(null);
  const [nyAnsattId, setNyAnsattId] = useState('');

  const last = async () => {
    const r = await api.get('/lonnsslipp', { params: { aar, maaned: mnd } });
    setSlipper(r.data);
  };
  useEffect(() => { last(); /* eslint-disable-next-line */ }, [aar, mnd]);
  useEffect(() => { api.get('/ansatte').then((r) => setAnsatte(r.data)); }, []);

  const opprettSlipp = async () => {
    if (!nyAnsattId) { alert('Velg ansatt.'); return; }
    try {
      const r = await api.post('/lonnsslipp', {
        ansatt_id: parseInt(nyAnsattId), aar, maaned: mnd,
      });
      setNyAnsattId('');
      last();
      aapneSlipp(r.data);
    } catch (e) {
      const data = e.response?.data;
      if (data?.id) {
        if (window.confirm(`${data.error}\n\nÅpne eksisterende?`)) {
          const r = await api.get(`/lonnsslipp/${data.id}`);
          aapneSlipp(r.data);
        }
      } else alert(data?.error || 'Feilet.');
    }
  };

  const aapneSlipp = (s) => {
    setValgtSlipp(s);
    setSkjema({
      fastlonn: s.fastlonn, timer: s.timer, timesats: s.timesats,
      overtid: s.overtid, bonus: s.bonus,
      annet_skattepliktig: s.annet_skattepliktig,
      trekkfrie_utlegg: s.trekkfrie_utlegg,
      forskuddstrekk: s.forskuddstrekk,
      fagforening: s.fagforening, andre_trekk: s.andre_trekk,
      utbetalingsdato: s.utbetalingsdato, notater: s.notater,
    });
    setOpen(true);
  };

  const lagreSlipp = async () => {
    try {
      const r = await api.put(`/lonnsslipp/${valgtSlipp.id}`, skjema);
      setValgtSlipp(r.data); last();
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const utbetal = async () => {
    if (!window.confirm(`Utbetale lønn til ${valgtSlipp.ansatt_navn}?\nDette bokfører lønn og oppretter bilag.`)) return;
    try {
      const r = await api.post(`/lonnsslipp/${valgtSlipp.id}/utbetal`);
      setValgtSlipp(r.data); last();
      alert('Lønn utbetalt og bokført.');
    } catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  const slettSlipp = async (s) => {
    if (!window.confirm(`Slette lønnsslipp for ${s.ansatt_navn}?`)) return;
    try { await api.delete(`/lonnsslipp/${s.id}`); setOpen(false); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  if (!slipper) return <span className="fm-text-muted">Laster…</span>;

  const sumBrutto = slipper.reduce((a, b) => a + b.brutto, 0);
  const sumNetto = slipper.reduce((a, b) => a + b.netto, 0);
  const sumTrekk = slipper.reduce((a, b) => a + b.forskuddstrekk, 0);
  const sumAga = slipper.reduce((a, b) => a + b.aga_belop, 0);

  const lastNedTelepay = async () => {
    const ikkeUtbetalt = slipper.filter((s) => s.status !== 'utbetalt' && (s.netto || 0) > 0);
    if (ikkeUtbetalt.length === 0) {
      alert('Ingen lønnsslipper å eksportere (alle er allerede utbetalt eller netto er 0).');
      return;
    }
    const sum = ikkeUtbetalt.reduce((a, b) => a + (b.netto || 0), 0);
    const forfall = window.prompt(
      `Eksporter Telepay-fil for ${ikkeUtbetalt.length} ansatte (sum kr ${sum.toFixed(2)}).\n\n` +
      `Forfallsdato (YYYY-MM-DD):`,
      new Date().toISOString().slice(0, 10)
    );
    if (!forfall) return;
    try {
      const r = await api.post('/lonnsslipp/telepay', {
        slipper: ikkeUtbetalt.map((s) => s.id),
        forfallsdato: forfall,
      }, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/plain;charset=latin-1' });
      const url = URL.createObjectURL(blob);
      const lenke = document.createElement('a');
      lenke.href = url;
      lenke.download = `lonn-telepay-${aar}-${String(mnd).padStart(2, '0')}.txt`;
      lenke.click();
      URL.revokeObjectURL(url);
      alert(
        `Telepay-fil lastet ned (${ikkeUtbetalt.length} betalinger, sum kr ${sum.toFixed(2)}).\n\n` +
        `Neste steg:\n` +
        `1. Logg inn i nettbanken\n` +
        `2. Velg «Last opp betalingsfil» / «Telepay-fil»\n` +
        `3. Last opp filen og godkjenn med BankID\n` +
        `4. Kom tilbake hit og marker hver lønnsslipp som «Utbetalt» for å bokføre.`
      );
    } catch (e) {
      const tekst = e.response?.data instanceof Blob
        ? await e.response.data.text() : e.response?.data?.error;
      alert('Eksport feilet: ' + (tekst || e.message));
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex" style={{ gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div><label>År</label>
            <input type="number" value={aar} onChange={(e) => setAar(parseInt(e.target.value))}
                   style={{ width: 100 }} /></div>
          <div><label>Måned</label>
            <select value={mnd} onChange={(e) => setMnd(parseInt(e.target.value))}>
              {MND_NAVN.slice(1).map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
            </select></div>
          <div style={{ flex: 1 }} />
          <div><label>Ny lønnsslipp</label>
            <select value={nyAnsattId} onChange={(e) => setNyAnsattId(e.target.value)}>
              <option value="">— velg ansatt —</option>
              {ansatte.map((a) => <option key={a.id} value={a.id}>{a.navn}</option>)}
            </select></div>
          <button className="btn-primary" onClick={opprettSlipp}><Plus size={14} /> Opprett</button>
          <button className="btn-ghost" onClick={lastNedTelepay}
                  title="Eksporter Telepay-fil for opplasting i nettbank (DNB / Sparebank1 / Nordea / Handelsbanken)">
            <Download size={14} /> Telepay-fil
          </button>
        </div>
      </Card>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 12 }}>
        <Card style={{ padding: 12, textAlign: 'center' }}>
          <div className="fm-text-muted" style={{ fontSize: 11 }}>Sum brutto</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{kr(sumBrutto)}</div>
        </Card>
        <Card style={{ padding: 12, textAlign: 'center' }}>
          <div className="fm-text-muted" style={{ fontSize: 11 }}>Forskuddstrekk</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{kr(sumTrekk)}</div>
        </Card>
        <Card style={{ padding: 12, textAlign: 'center' }}>
          <div className="fm-text-muted" style={{ fontSize: 11 }}>AGA</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{kr(sumAga)}</div>
        </Card>
        <Card style={{ padding: 12, textAlign: 'center' }}>
          <div className="fm-text-muted" style={{ fontSize: 11 }}>Netto utbetalt</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-success)' }}>{kr(sumNetto)}</div>
        </Card>
      </div>

      <Card style={{ padding: 0 }}>
        {slipper.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Wallet size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
            <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>
              Ingen lønnsslipper for {MND_NAVN[mnd]} {aar}.
            </p>
          </div>
        ) : (
          <table>
            <thead><tr>
              <th>Ansatt</th>
              <th style={{ textAlign: 'right' }}>Brutto</th>
              <th style={{ textAlign: 'right' }}>Trekk</th>
              <th style={{ textAlign: 'right' }}>AGA</th>
              <th style={{ textAlign: 'right' }}>Netto</th>
              <th>Status</th><th></th>
            </tr></thead>
            <tbody>
              {slipper.map((s) => (
                <tr key={s.id}>
                  <td><b>{s.ansatt_navn}</b></td>
                  <td style={{ textAlign: 'right' }}>{kr(s.brutto)}</td>
                  <td style={{ textAlign: 'right' }}>{kr(s.forskuddstrekk)}</td>
                  <td style={{ textAlign: 'right' }}>{kr(s.aga_belop)}</td>
                  <td style={{ textAlign: 'right', color: 'var(--theme-success)' }}>{kr(s.netto)}</td>
                  <td><span className={`badge ${STATUS_BADGE[s.status]}`}>{s.status}</span></td>
                  <td>
                    <button className="btn-ghost" onClick={() => aapneSlipp(s)}>
                      <Pencil size={14} /> Åpne
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Modal open={open && !!valgtSlipp} onClose={() => setOpen(false)}
             title={valgtSlipp ? `Lønnsslipp – ${valgtSlipp.ansatt_navn} (${MND_NAVN[valgtSlipp.maaned]} ${valgtSlipp.aar})` : ''}
             width={700}
             footer={
               valgtSlipp?.status === 'utbetalt' ? (
                 <button className="btn-ghost" onClick={() => setOpen(false)}>Lukk</button>
               ) : (
                 <>
                   <button className="btn-ghost" onClick={() => slettSlipp(valgtSlipp)}>
                     <Trash2 size={14} /> Slett
                   </button>
                   <div style={{ flex: 1 }} />
                   <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
                   <button className="btn-ghost" onClick={lagreSlipp}>Lagre</button>
                   <button className="btn-primary" onClick={async () => { await lagreSlipp(); await utbetal(); }}>
                     <Wallet size={14} /> Lagre & Utbetal
                   </button>
                 </>
               )}>
        {valgtSlipp && skjema && (
          <>
            <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
              <div><label>Fastlønn</label>
                <input type="number" step="0.01" value={skjema.fastlonn}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, fastlonn: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Timer</label>
                <input type="number" step="0.5" value={skjema.timer}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, timer: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Timesats</label>
                <input type="number" step="0.01" value={skjema.timesats}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, timesats: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Overtid</label>
                <input type="number" step="0.01" value={skjema.overtid}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, overtid: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Bonus</label>
                <input type="number" step="0.01" value={skjema.bonus}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, bonus: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Annet skattepliktig</label>
                <input type="number" step="0.01" value={skjema.annet_skattepliktig}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, annet_skattepliktig: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Trekkfrie utlegg (km/diett)</label>
                <input type="number" step="0.01" value={skjema.trekkfrie_utlegg}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, trekkfrie_utlegg: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Forskuddstrekk (manuelt)</label>
                <input type="number" step="0.01" value={skjema.forskuddstrekk}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, forskuddstrekk: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Fagforening</label>
                <input type="number" step="0.01" value={skjema.fagforening}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, fagforening: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Andre trekk</label>
                <input type="number" step="0.01" value={skjema.andre_trekk}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, andre_trekk: parseFloat(e.target.value || 0) })} /></div>
              <div><label>Utbetalingsdato</label>
                <input type="date" value={skjema.utbetalingsdato}
                       readOnly={valgtSlipp.status === 'utbetalt'}
                       onChange={(e) => setSkjema({ ...skjema, utbetalingsdato: e.target.value })} /></div>
            </div>

            <Card style={{ marginTop: 12, padding: 10, background: 'var(--theme-bg-input)' }}>
              <table>
                <tbody>
                  <tr><td><b>Brutto</b></td><td style={{ textAlign: 'right' }}><b>{kr(valgtSlipp.brutto)}</b></td></tr>
                  <tr><td>− Forskuddstrekk</td><td style={{ textAlign: 'right' }}>−{kr(valgtSlipp.forskuddstrekk)}</td></tr>
                  <tr><td>− Fagforening / andre</td><td style={{ textAlign: 'right' }}>−{kr(valgtSlipp.fagforening + valgtSlipp.andre_trekk)}</td></tr>
                  <tr><td>+ Trekkfrie utlegg</td><td style={{ textAlign: 'right' }}>+{kr(valgtSlipp.trekkfrie_utlegg)}</td></tr>
                  <tr style={{ borderTop: '1px solid var(--theme-border)' }}>
                    <td><b>Netto til utbetaling</b></td>
                    <td style={{ textAlign: 'right', color: 'var(--theme-success)' }}><b>{kr(valgtSlipp.netto + valgtSlipp.trekkfrie_utlegg)}</b></td>
                  </tr>
                  <tr><td className="fm-text-muted">Avsatt feriepenger</td>
                      <td style={{ textAlign: 'right' }} className="fm-text-muted">{kr(valgtSlipp.feriepenger_avsatt)}</td></tr>
                  <tr><td className="fm-text-muted">Arbeidsgiveravgift</td>
                      <td style={{ textAlign: 'right' }} className="fm-text-muted">{kr(valgtSlipp.aga_belop)}</td></tr>
                </tbody>
              </table>
            </Card>
            {valgtSlipp.bilag_id && (
              <p className="fm-text-muted" style={{ fontSize: 12, marginTop: 8 }}>
                Bokført på bilag #{valgtSlipp.bilag_id}.
              </p>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

// ── A-meldinger ─────────────────────────────────────────────────────────────
const Ameldinger = () => {
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [aar, setAar] = useState(new Date().getFullYear());
  const [mnd, setMnd] = useState(new Date().getMonth() + 1);
  const [valgt, setValgt] = useState(null);

  const last = () => api.get('/amelding').then((r) => setListe(r.data));
  useEffect(() => { last(); }, []);

  const opprett = async () => {
    try {
      const r = await api.post('/amelding', { aar, maaned: mnd });
      setOpen(false); last(); setValgt(r.data);
    } catch (e) {
      const d = e.response?.data;
      if (d?.id) {
        if (window.confirm(`${d.error}\n\nÅpne eksisterende?`)) {
          const r = await api.get(`/amelding/${d.id}`);
          setOpen(false); setValgt(r.data);
        }
      } else alert(d?.error || 'Feilet.');
    }
  };

  const aapne = async (m) => {
    const r = await api.get(`/amelding/${m.id}`);
    setValgt(r.data);
  };

  const lastNed = (m) => window.open(`/api/amelding/${m.id}/xml`, '_blank');

  const send = async () => {
    try {
      await api.post(`/amelding/${valgt.id}/send`);
      alert('Sendt!'); last();
    } catch (e) {
      const d = e.response?.data;
      if (d?.konfig_mangler) {
        alert(`${d.error}\n\nTrenger: ${(d.trenger || []).join(', ')}\n\n${d.neste_steg || ''}`);
      } else {
        alert(d?.error || 'Feilet.');
      }
    }
  };

  const markerSendt = async () => {
    const ref = window.prompt('Altinn-referanse (valgfritt):', '');
    if (ref === null) return;
    await api.post(`/amelding/${valgt.id}/marker-sendt`, { altinn_ref: ref });
    const r = await api.get(`/amelding/${valgt.id}`);
    setValgt(r.data); last();
  };

  const slett = async (m) => {
    if (!window.confirm(`Slette a-melding for ${m.aar}-${String(m.maaned).padStart(2,'0')}?`)) return;
    try { await api.delete(`/amelding/${m.id}`); if (valgt?.id === m.id) setValgt(null); last(); }
    catch (e) { alert(e.response?.data?.error || 'Feilet.'); }
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  return (
    <>
      <Card style={{ marginBottom: 12, padding: 12 }}>
        <div className="flex justify-between items-center">
          <span className="fm-text-muted" style={{ fontSize: 12 }}>
            A-meldinger sendes til Skatteetaten den 5. i påfølgende måned.
          </span>
          <button className="btn-primary" onClick={() => setOpen(true)}><Plus size={14} /> Ny a-melding</button>
        </div>
      </Card>

      <div className="grid" style={{ gridTemplateColumns: '320px 1fr', gap: 12 }}>
        <Card style={{ padding: 0, alignSelf: 'start' }}>
          {liste.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <FileText size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
              <p className="fm-text-muted" style={{ margin: 0, fontSize: 12 }}>Ingen a-meldinger.</p>
            </div>
          ) : liste.map((m) => (
            <div key={m.id} onClick={() => aapne(m)}
                 style={{ padding: 10, borderBottom: '1px solid var(--theme-border)',
                          cursor: 'pointer',
                          background: valgt?.id === m.id ? 'var(--theme-bg-hover)' : 'transparent' }}>
              <div className="flex justify-between" style={{ alignItems: 'center' }}>
                <strong style={{ fontSize: 13 }}>{m.aar}-{String(m.maaned).padStart(2,'0')}</strong>
                <span className={`badge ${STATUS_BADGE[m.status]}`}>{m.status}</span>
              </div>
              <div className="fm-text-muted" style={{ fontSize: 11, marginTop: 2 }}>
                {m.antall_inntektsmottakere} ansatt(e) · brutto {kr(m.sum_brutto)}
              </div>
            </div>
          ))}
        </Card>

        <div>
          {!valgt ? (
            <Card style={{ padding: 32, textAlign: 'center' }}>
              <FileText size={48} className="fm-text-muted" style={{ marginBottom: 12 }} />
              <p className="fm-text-muted">Velg en a-melding fra listen, eller opprett en ny.</p>
            </Card>
          ) : (
            <>
              <Card style={{ marginBottom: 12, padding: 14 }}>
                <div className="flex justify-between items-center" style={{ marginBottom: 12 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18 }}>
                      A-melding {valgt.aar}-{String(valgt.maaned).padStart(2,'0')} ({MND_NAVN[valgt.maaned]})
                    </h2>
                    <div className="fm-text-muted" style={{ fontSize: 12 }}>
                      {valgt.antall_inntektsmottakere} inntektsmottaker(e)
                      {valgt.sendt_dato && ` · Sendt ${dato(valgt.sendt_dato.slice(0,10))}`}
                    </div>
                  </div>
                  <span className={`badge ${STATUS_BADGE[valgt.status]}`}>{valgt.status}</span>
                </div>
                <div className="flex" style={{ gap: 6, flexWrap: 'wrap' }}>
                  <button className="btn-ghost" onClick={() => lastNed(valgt)}>
                    <Download size={14} /> Last ned XML
                  </button>
                  {valgt.status === 'utkast' && (
                    <>
                      <button className="btn-primary" onClick={send}>
                        <Send size={14} /> Send til Altinn
                      </button>
                      <button className="btn-ghost" onClick={markerSendt}>
                        <CheckCircle2 size={14} /> Marker som sendt
                      </button>
                      <button className="btn-ghost" onClick={() => slett(valgt)}>
                        <Trash2 size={14} style={{ color: 'var(--theme-danger)' }} />
                      </button>
                    </>
                  )}
                </div>
              </Card>

              <div className="grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 12 }}>
                <Card style={{ padding: 14, textAlign: 'center' }}>
                  <div className="fm-text-muted" style={{ fontSize: 11 }}>Sum brutto lønn</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{kr(valgt.sum_brutto)}</div>
                </Card>
                <Card style={{ padding: 14, textAlign: 'center' }}>
                  <div className="fm-text-muted" style={{ fontSize: 11 }}>Forskuddstrekk</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{kr(valgt.sum_forskuddstrekk)}</div>
                </Card>
                <Card style={{ padding: 14, textAlign: 'center' }}>
                  <div className="fm-text-muted" style={{ fontSize: 11 }}>Arbeidsgiveravgift</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{kr(valgt.sum_aga)}</div>
                </Card>
              </div>

              {valgt.altinn_ref && (
                <Card style={{ padding: 10, marginBottom: 12, fontSize: 12 }}>
                  <strong>Altinn referanse:</strong> {valgt.altinn_ref}
                </Card>
              )}

              {valgt.xml && (
                <Card style={{ padding: 0 }}>
                  <details>
                    <summary style={{ padding: 10, cursor: 'pointer', fontSize: 13 }}>
                      Vis generert XML ({Math.round(valgt.xml.length / 1024)} kB)
                    </summary>
                    <pre style={{ padding: 10, margin: 0, fontSize: 10, maxHeight: 400, overflow: 'auto' }}>
                      {valgt.xml}
                    </pre>
                  </details>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Ny a-melding"
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={opprett}>Opprett</button>
             </>}>
        <p style={{ fontSize: 13 }}>
          A-meldingen samler alle utbetalte lønnsslipper i valgt måned.
        </p>
        <Card style={{ padding: 8, fontSize: 12, marginBottom: 10, background: 'rgba(251,191,36,0.08)' }}>
          <AlertTriangle size={14} style={{ color: 'var(--theme-warning)' }} /> Pass på at alle
          lønnsslippene for måneden er utbetalt og bokført før du oppretter a-meldingen.
        </Card>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div><label>År</label>
            <input type="number" value={aar} onChange={(e) => setAar(parseInt(e.target.value))} /></div>
          <div><label>Måned</label>
            <select value={mnd} onChange={(e) => setMnd(parseInt(e.target.value))}>
              {MND_NAVN.slice(1).map((n, i) => <option key={i+1} value={i+1}>{n}</option>)}
            </select></div>
        </div>
      </Modal>
    </>
  );
};

// ── OTP / pensjon-rapport ───────────────────────────────────────────────────
const OtpRapport = () => {
  const [aar, setAar] = useState(new Date().getFullYear());
  const [kvartal, setKvartal] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [data, setData] = useState(null);

  const last = () => {
    setData(null);
    api.get(`/otp/rapport?aar=${aar}&kvartal=${kvartal}`).then((r) => setData(r.data));
  };
  useEffect(() => { last(); /* eslint-disable-next-line */ }, [aar, kvartal]);

  const lastNedCsv = async () => {
    const r = await api.get(`/otp/rapport/csv?aar=${aar}&kvartal=${kvartal}`,
      { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([r.data]));
    const a = document.createElement('a');
    a.href = url; a.download = `otp_rapport_${aar}_K${kvartal}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <>
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>År:&nbsp;
            <select value={aar} onChange={(e) => setAar(Number(e.target.value))}>
              {[0, -1, -2].map((d) => {
                const v = new Date().getFullYear() + d;
                return <option key={v} value={v}>{v}</option>;
              })}
            </select>
          </label>
          <label>Kvartal:&nbsp;
            <select value={kvartal} onChange={(e) => setKvartal(Number(e.target.value))}>
              <option value={1}>Q1 (jan-mar)</option>
              <option value={2}>Q2 (apr-jun)</option>
              <option value={3}>Q3 (jul-sep)</option>
              <option value={4}>Q4 (okt-des)</option>
            </select>
          </label>
          <button className="btn-ghost" onClick={last}><RefreshCw size={14} /> Oppdater</button>
          <button className="btn-primary" onClick={lastNedCsv}
                  disabled={!data || !data.rader.length}>
            <Download size={14} /> Last ned CSV
          </button>
        </div>
      </Card>

      {!data && <Card><span className="fm-text-muted">Laster…</span></Card>}

      {data && (
        <Card style={{ padding: 0 }}>
          {data.rader.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Wallet size={32} className="fm-text-muted" style={{ marginBottom: 8 }} />
              <p className="fm-text-muted" style={{ margin: 0, fontSize: 13 }}>
                Ingen ansatte med OTP-trekk i denne perioden.
              </p>
              <p className="fm-text-muted" style={{ margin: '8px 0 0', fontSize: 11 }}>
                Aktiver OTP på den enkelte ansatte under "Ansatte"-fanen.
              </p>
            </div>
          ) : (
            <table>
              <thead><tr>
                <th>Ansatt</th><th>Sats</th>
                <th style={{ textAlign: 'right' }}>Grunnlag</th>
                <th style={{ textAlign: 'right' }}>Innskudd</th>
                <th style={{ textAlign: 'right' }}>Slipper</th>
              </tr></thead>
              <tbody>
                {data.rader.map((r) => (
                  <tr key={r.ansatt_id}>
                    <td><b>{r.navn}</b>
                      {!r.otp_aktiv && <span className="badge badge-warning" style={{ marginLeft: 6 }}>ikke aktiv</span>}
                    </td>
                    <td>{r.otp_sats}%</td>
                    <td style={{ textAlign: 'right' }}>{kr(r.grunnlag)}</td>
                    <td style={{ textAlign: 'right' }}><b>{kr(r.belop)}</b></td>
                    <td style={{ textAlign: 'right' }}>{r.antall_slipper}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--theme-border)', fontWeight: 700 }}>
                  <td colSpan={2}>Totalt {data.antall_ansatte} ansatte</td>
                  <td style={{ textAlign: 'right' }}>{kr(data.sum_grunnlag)}</td>
                  <td style={{ textAlign: 'right' }}>{kr(data.sum_belop)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          )}
          <div style={{ padding: 12, fontSize: 12 }} className="fm-text-muted">
            CSV-filen kan importeres direkte hos Storebrand, KLP, Nordnet og de fleste andre OTP-leverandører.
            Innbetaling skjer normalt månedlig eller kvartalsvis i henhold til avtale med pensjonsleverandør.
          </div>
        </Card>
      )}
    </>
  );
};

export default Lonn;
